import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, clientsApi } from '../lib/api';

interface Client {
  id: string;
  slug: string;
  companyName: string;
  websiteUrl: string;
  city: string;
  niche: string;
  logoUrl: string | null;
  isActive: boolean;
  napName?: string;
  napPhone?: string;
  napAddress?: string;
  gmbUrl?: string;
  mapEmbedHtml?: string;
  youtubeTags?: string[];
  youtubeHashtags?: string[];
  internalLinksDefault?: string;
  defaultPaaTexts?: string[];
  aliases?: string[];
  sameAs?: string[];
  categories?: string[];
  integrations: {
    wordpress: boolean;
    youtube: boolean;
    podbean: boolean;
    facebook: boolean;
    gbp: boolean;
    renderform?: boolean;
    sheets?: boolean;
  };
  credentials?: ClientCredentials;
}

interface ClientCredentials {
  // WordPress
  wordpressBaseUrl?: string;
  wordpressUser?: string;
  wordpressAppPassword?: string;
  wordpressDefaultCategory?: number;
  // YouTube
  youtubeClientId?: string;
  youtubeClientSecret?: string;
  youtubeRefreshToken?: string;
  youtubePrivacyStatus?: string;
  // Podbean
  podbeanClientId?: string;
  podbeanClientSecret?: string;
  podbeanPodcastId?: string;
  // Google Sheets
  sheetsSpreadsheetId?: string;
  sheetsTabName?: string;
  driveParentFolderId?: string;
  // RenderForm
  renderformTemplateId?: string;
  renderformTextFieldName?: string;
  renderformQrFieldName?: string;
  // Facebook
  facebookPageId?: string;
  facebookAccessToken?: string;
  // GBP
  gbpAccountId?: string;
  gbpLocationId?: string;
  gbpAccessToken?: string;
  gbpRefreshToken?: string;
}

interface Workflow {
  id: string;
  paaText: string;
  paaSlug: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
  results: Record<string, unknown>;
}

interface WorkflowsResponse {
  workflows: Workflow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

type TabType = 'overview' | 'credentials' | 'workflows';

export function ClientDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Editable client info
  const [editedClient, setEditedClient] = useState<Partial<Client>>({});
  // Editable credentials
  const [editedCredentials, setEditedCredentials] = useState<ClientCredentials>({});

  useEffect(() => {
    loadData();
  }, [slug]);

  async function loadData() {
    setLoading(true);
    try {
      const clientRes = await clientsApi.get(slug!);
      const clientData = clientRes.client as Client;
      setClient(clientData);
      setEditedClient(clientData);
      setEditedCredentials(clientData.credentials || {});

      // Load workflows for this client
      const workflowsRes = await api.get<WorkflowsResponse>(`/workflows?clientId=${clientData.id}&limit=50`);
      setWorkflows(workflowsRes.workflows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load client');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveClient() {
    if (!client) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await clientsApi.update(client.slug, {
        companyName: editedClient.companyName,
        websiteUrl: editedClient.websiteUrl,
        city: editedClient.city,
        niche: editedClient.niche,
        napName: editedClient.napName,
        napPhone: editedClient.napPhone,
        napAddress: editedClient.napAddress,
        logoUrl: editedClient.logoUrl || '',
        gmbUrl: editedClient.gmbUrl || '',
        mapEmbedHtml: editedClient.mapEmbedHtml,
        youtubeTags: editedClient.youtubeTags,
        youtubeHashtags: editedClient.youtubeHashtags,
        internalLinksDefault: editedClient.internalLinksDefault,
        aliases: editedClient.aliases,
        sameAs: editedClient.sameAs,
        categories: editedClient.categories,
      });
      setSaveMessage('Client information saved successfully');
      setEditMode(false);
      await loadData();
    } catch (err) {
      setSaveMessage(`Error: ${err instanceof Error ? err.message : 'Failed to save'}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveCredentials() {
    if (!client) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      // Filter out masked values (••••••••) so we don't overwrite with placeholders
      const credentialsToSave: Record<string, unknown> = {};
      Object.entries(editedCredentials).forEach(([key, value]) => {
        if (value && value !== '••••••••') {
          credentialsToSave[key] = value;
        }
      });

      await api.put(`/clients/${client.slug}/credentials`, credentialsToSave);
      setSaveMessage('Credentials saved successfully');
      await loadData();
    } catch (err) {
      setSaveMessage(`Error: ${err instanceof Error ? err.message : 'Failed to save credentials'}`);
    } finally {
      setSaving(false);
    }
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      COMPLETED: 'bg-green-100 text-green-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
      PENDING: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Client not found'}</p>
        <Link to="/clients" className="text-primary-600 hover:underline mt-4 inline-block">
          Back to Clients
        </Link>
      </div>
    );
  }

  const completedWorkflows = workflows.filter(w => w.status === 'COMPLETED').length;
  const failedWorkflows = workflows.filter(w => w.status === 'FAILED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <Link to="/clients" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            &larr; Back to Clients
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{client.companyName}</h1>
          <p className="text-gray-600">{client.city} &bull; {client.niche}</p>
        </div>
        <Link
          to={`/workflows/new?clientId=${client.id}`}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          New Workflow
        </Link>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg ${saveMessage.startsWith('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {saveMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'credentials', 'workflows'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          client={client}
          editMode={editMode}
          editedClient={editedClient}
          setEditedClient={setEditedClient}
          setEditMode={setEditMode}
          onSave={handleSaveClient}
          saving={saving}
        />
      )}

      {activeTab === 'credentials' && (
        <CredentialsTab
          client={client}
          credentials={editedCredentials}
          setCredentials={setEditedCredentials}
          onSave={handleSaveCredentials}
          saving={saving}
        />
      )}

      {activeTab === 'workflows' && (
        <WorkflowsTab
          client={client}
          workflows={workflows}
          completedWorkflows={completedWorkflows}
          failedWorkflows={failedWorkflows}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Overview Tab Component
function OverviewTab({
  client,
  editMode,
  editedClient,
  setEditedClient,
  setEditMode,
  onSave,
  saving,
}: {
  client: Client;
  editMode: boolean;
  editedClient: Partial<Client>;
  setEditedClient: (c: Partial<Client>) => void;
  setEditMode: (e: boolean) => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Client Information</h2>
        {editMode ? (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditMode(true)}
            className="px-4 py-2 text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50"
          >
            Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Company Name"
          value={editedClient.companyName || ''}
          onChange={(v) => setEditedClient({ ...editedClient, companyName: v })}
          editMode={editMode}
        />
        <FormField
          label="Slug"
          value={client.slug}
          editMode={false}
          disabled
        />
        <FormField
          label="Website URL"
          value={editedClient.websiteUrl || ''}
          onChange={(v) => setEditedClient({ ...editedClient, websiteUrl: v })}
          editMode={editMode}
          type="url"
        />
        <FormField
          label="City"
          value={editedClient.city || ''}
          onChange={(v) => setEditedClient({ ...editedClient, city: v })}
          editMode={editMode}
        />
        <FormField
          label="Niche"
          value={editedClient.niche || ''}
          onChange={(v) => setEditedClient({ ...editedClient, niche: v })}
          editMode={editMode}
        />
        <FormField
          label="Logo URL"
          value={editedClient.logoUrl || ''}
          onChange={(v) => setEditedClient({ ...editedClient, logoUrl: v })}
          editMode={editMode}
          type="url"
        />
        <FormField
          label="NAP Business Name"
          value={editedClient.napName || ''}
          onChange={(v) => setEditedClient({ ...editedClient, napName: v })}
          editMode={editMode}
        />
        <FormField
          label="NAP Phone"
          value={editedClient.napPhone || ''}
          onChange={(v) => setEditedClient({ ...editedClient, napPhone: v })}
          editMode={editMode}
        />
        <FormField
          label="NAP Address"
          value={editedClient.napAddress || ''}
          onChange={(v) => setEditedClient({ ...editedClient, napAddress: v })}
          editMode={editMode}
          className="md:col-span-2"
        />
        <FormField
          label="Google Business Profile URL"
          value={editedClient.gmbUrl || ''}
          onChange={(v) => setEditedClient({ ...editedClient, gmbUrl: v })}
          editMode={editMode}
          type="url"
          className="md:col-span-2"
        />
        <FormField
          label="Internal Links Default"
          value={editedClient.internalLinksDefault || ''}
          onChange={(v) => setEditedClient({ ...editedClient, internalLinksDefault: v })}
          editMode={editMode}
          multiline
          className="md:col-span-2"
        />
        <FormField
          label="YouTube Tags (comma-separated)"
          value={(editedClient.youtubeTags || []).join(', ')}
          onChange={(v) => setEditedClient({ ...editedClient, youtubeTags: v.split(',').map(s => s.trim()).filter(Boolean) })}
          editMode={editMode}
        />
        <FormField
          label="YouTube Hashtags (comma-separated)"
          value={(editedClient.youtubeHashtags || []).join(', ')}
          onChange={(v) => setEditedClient({ ...editedClient, youtubeHashtags: v.split(',').map(s => s.trim()).filter(Boolean) })}
          editMode={editMode}
        />
        <FormField
          label="Aliases (comma-separated)"
          value={(editedClient.aliases || []).join(', ')}
          onChange={(v) => setEditedClient({ ...editedClient, aliases: v.split(',').map(s => s.trim()).filter(Boolean) })}
          editMode={editMode}
        />
        <FormField
          label="Categories (comma-separated)"
          value={(editedClient.categories || []).join(', ')}
          onChange={(v) => setEditedClient({ ...editedClient, categories: v.split(',').map(s => s.trim()).filter(Boolean) })}
          editMode={editMode}
        />
      </div>

      {/* Integrations Status */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Integration Status</h3>
        <div className="flex flex-wrap gap-2">
          <IntegrationBadge name="WordPress" active={client.integrations.wordpress} />
          <IntegrationBadge name="YouTube" active={client.integrations.youtube} />
          <IntegrationBadge name="Podbean" active={client.integrations.podbean} />
          <IntegrationBadge name="Facebook" active={client.integrations.facebook} />
          <IntegrationBadge name="GBP" active={client.integrations.gbp} />
          <IntegrationBadge name="RenderForm" active={client.integrations.renderform || false} />
          <IntegrationBadge name="Sheets" active={client.integrations.sheets || false} />
        </div>
      </div>
    </div>
  );
}

// Credentials Tab Component
function CredentialsTab({
  client,
  credentials,
  setCredentials,
  onSave,
  saving,
}: {
  client: Client;
  credentials: ClientCredentials;
  setCredentials: (c: ClientCredentials) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const updateCredential = (key: keyof ClientCredentials, value: string | number) => {
    setCredentials({ ...credentials, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save All Credentials'}
        </button>
      </div>

      {/* WordPress */}
      <CredentialSection
        title="WordPress"
        icon="W"
        active={client.integrations.wordpress}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="WordPress Base URL"
            value={credentials.wordpressBaseUrl || ''}
            onChange={(v) => updateCredential('wordpressBaseUrl', v)}
            editMode={true}
            type="url"
            placeholder="https://example.com/wp-json/wp/v2"
          />
          <FormField
            label="WordPress User"
            value={credentials.wordpressUser || ''}
            onChange={(v) => updateCredential('wordpressUser', v)}
            editMode={true}
            placeholder="admin"
          />
          <FormField
            label="WordPress App Password"
            value={credentials.wordpressAppPassword || ''}
            onChange={(v) => updateCredential('wordpressAppPassword', v)}
            editMode={true}
            type="password"
            placeholder="Enter new password to update"
          />
          <FormField
            label="Default Category ID"
            value={credentials.wordpressDefaultCategory?.toString() || ''}
            onChange={(v) => updateCredential('wordpressDefaultCategory', parseInt(v) || 0)}
            editMode={true}
            placeholder="1"
          />
        </div>
      </CredentialSection>

      {/* YouTube */}
      <CredentialSection
        title="YouTube"
        icon="Y"
        active={client.integrations.youtube}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Client ID"
            value={credentials.youtubeClientId || ''}
            onChange={(v) => updateCredential('youtubeClientId', v)}
            editMode={true}
            placeholder="xxxxx.apps.googleusercontent.com"
          />
          <FormField
            label="Client Secret"
            value={credentials.youtubeClientSecret || ''}
            onChange={(v) => updateCredential('youtubeClientSecret', v)}
            editMode={true}
            type="password"
            placeholder="Enter new secret to update"
          />
          <FormField
            label="Refresh Token"
            value={credentials.youtubeRefreshToken || ''}
            onChange={(v) => updateCredential('youtubeRefreshToken', v)}
            editMode={true}
            type="password"
            placeholder="Enter new token to update"
          />
          <FormField
            label="Privacy Status"
            value={credentials.youtubePrivacyStatus || 'public'}
            onChange={(v) => updateCredential('youtubePrivacyStatus', v)}
            editMode={true}
            options={['public', 'private', 'unlisted']}
          />
        </div>
      </CredentialSection>

      {/* Podbean */}
      <CredentialSection
        title="Podbean"
        icon="P"
        active={client.integrations.podbean}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Client ID"
            value={credentials.podbeanClientId || ''}
            onChange={(v) => updateCredential('podbeanClientId', v)}
            editMode={true}
          />
          <FormField
            label="Client Secret"
            value={credentials.podbeanClientSecret || ''}
            onChange={(v) => updateCredential('podbeanClientSecret', v)}
            editMode={true}
            type="password"
            placeholder="Enter new secret to update"
          />
          <FormField
            label="Podcast ID"
            value={credentials.podbeanPodcastId || ''}
            onChange={(v) => updateCredential('podbeanPodcastId', v)}
            editMode={true}
            className="md:col-span-2"
          />
        </div>
      </CredentialSection>

      {/* Google Sheets / Drive */}
      <CredentialSection
        title="Google Sheets & Drive"
        icon="G"
        active={client.integrations.sheets || false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Spreadsheet ID"
            value={credentials.sheetsSpreadsheetId || ''}
            onChange={(v) => updateCredential('sheetsSpreadsheetId', v)}
            editMode={true}
            placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
          />
          <FormField
            label="Tab Name"
            value={credentials.sheetsTabName || ''}
            onChange={(v) => updateCredential('sheetsTabName', v)}
            editMode={true}
            placeholder="Sheet1"
          />
          <FormField
            label="Drive Parent Folder ID"
            value={credentials.driveParentFolderId || ''}
            onChange={(v) => updateCredential('driveParentFolderId', v)}
            editMode={true}
            className="md:col-span-2"
          />
        </div>
      </CredentialSection>

      {/* RenderForm */}
      <CredentialSection
        title="RenderForm"
        icon="R"
        active={client.integrations.renderform || false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Template ID"
            value={credentials.renderformTemplateId || ''}
            onChange={(v) => updateCredential('renderformTemplateId', v)}
            editMode={true}
          />
          <FormField
            label="Text Field Name"
            value={credentials.renderformTextFieldName || ''}
            onChange={(v) => updateCredential('renderformTextFieldName', v)}
            editMode={true}
          />
          <FormField
            label="QR Field Name"
            value={credentials.renderformQrFieldName || ''}
            onChange={(v) => updateCredential('renderformQrFieldName', v)}
            editMode={true}
          />
        </div>
      </CredentialSection>

      {/* Facebook */}
      <CredentialSection
        title="Facebook"
        icon="F"
        active={client.integrations.facebook}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Page ID"
            value={credentials.facebookPageId || ''}
            onChange={(v) => updateCredential('facebookPageId', v)}
            editMode={true}
          />
          <FormField
            label="Access Token"
            value={credentials.facebookAccessToken || ''}
            onChange={(v) => updateCredential('facebookAccessToken', v)}
            editMode={true}
            type="password"
            placeholder="Enter new token to update"
          />
        </div>
      </CredentialSection>

      {/* GBP (Google Business Profile) */}
      <CredentialSection
        title="Google Business Profile"
        icon="B"
        active={client.integrations.gbp}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Account ID"
            value={credentials.gbpAccountId || ''}
            onChange={(v) => updateCredential('gbpAccountId', v)}
            editMode={true}
          />
          <FormField
            label="Location ID"
            value={credentials.gbpLocationId || ''}
            onChange={(v) => updateCredential('gbpLocationId', v)}
            editMode={true}
          />
          <FormField
            label="Access Token"
            value={credentials.gbpAccessToken || ''}
            onChange={(v) => updateCredential('gbpAccessToken', v)}
            editMode={true}
            type="password"
            placeholder="Enter new token to update"
          />
          <FormField
            label="Refresh Token"
            value={credentials.gbpRefreshToken || ''}
            onChange={(v) => updateCredential('gbpRefreshToken', v)}
            editMode={true}
            type="password"
            placeholder="Enter new token to update"
          />
        </div>
      </CredentialSection>
    </div>
  );
}

// Workflows Tab Component
function WorkflowsTab({
  client,
  workflows,
  completedWorkflows,
  failedWorkflows,
  getStatusBadge,
  formatDate,
}: {
  client: Client;
  workflows: Workflow[];
  completedWorkflows: number;
  failedWorkflows: number;
  getStatusBadge: (status: string) => string;
  formatDate: (date: string) => string;
}) {
  return (
    <div className="space-y-6">
      {/* Workflow Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Workflows</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{workflows.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Completed</h3>
          <p className="text-3xl font-bold text-green-600 mt-2">{completedWorkflows}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500">Failed</h3>
          <p className="text-3xl font-bold text-red-600 mt-2">{failedWorkflows}</p>
        </div>
      </div>

      {/* Workflow History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Workflow History</h2>
        </div>

        {workflows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No workflows yet.{' '}
            <Link to={`/workflows/new?clientId=${client.id}`} className="text-primary-600 hover:underline">
              Start a new workflow
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {workflows.map((workflow) => (
              <div key={workflow.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/workflows/${workflow.id}`}
                      className="text-primary-600 hover:text-primary-800 font-medium truncate block"
                    >
                      {workflow.paaText}
                    </Link>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span>{formatDate(workflow.createdAt)}</span>
                      <span>Step {workflow.currentStep}/{workflow.totalSteps}</span>
                    </div>
                    {workflow.errorMessage && (
                      <p className="text-xs text-red-600 mt-1">{workflow.errorMessage}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(workflow.status)}`}>
                      {workflow.status}
                    </span>
                    <Link
                      to={`/workflows/${workflow.id}`}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      View &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Form Field Component
function FormField({
  label,
  value,
  onChange,
  editMode,
  type = 'text',
  multiline = false,
  disabled = false,
  placeholder = '',
  className = '',
  options,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  editMode: boolean;
  type?: 'text' | 'url' | 'password';
  multiline?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  options?: string[];
}) {
  if (!editMode || disabled) {
    return (
      <div className={className}>
        <h3 className="text-sm font-medium text-gray-500">{label}</h3>
        {type === 'url' && value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
            {value}
          </a>
        ) : type === 'password' && value ? (
          <p className="text-gray-900">••••••••</p>
        ) : (
          <p className="text-gray-900">{value || '-'}</p>
        )}
      </div>
    );
  }

  if (options) {
    return (
      <div className={className}>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <select
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  if (multiline) {
    return (
      <div className={className}>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
        placeholder={placeholder}
      />
    </div>
  );
}

// Credential Section Component
function CredentialSection({
  title,
  icon,
  active,
  children,
}: {
  title: string;
  icon: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold ${
            active ? 'bg-green-500' : 'bg-gray-400'
          }`}>
            {icon}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <span className={`px-3 py-1 text-sm rounded-full ${
          active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {active ? 'Configured' : 'Not Configured'}
        </span>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

// Integration Badge Component
function IntegrationBadge({ name, active }: { name: string; active: boolean }) {
  return (
    <span className={`inline-flex px-3 py-1 text-sm rounded-full ${
      active
        ? 'bg-green-100 text-green-800'
        : 'bg-gray-100 text-gray-500'
    }`}>
      {active ? '✓' : '✗'} {name}
    </span>
  );
}
